/**
 * Panel.tsx — Default CMS renderer; renders a vertical stack of content from
 * arbitrary JSON keys.
 *
 * JSON trigger: `uiCompType.method` omitted (or any unrecognised method).
 *
 * **Key dispatch logic:**
 * Each `Object.keys(sectionData)` entry is split on `-` to separate the base key
 * name from style suffix tokens (parsed by `extractKeyStyles`). The base key is
 * then matched via a `switch` statement:
 *
 * | Key (stripped)   | Renders as |
 * |---|---|
 * | `space`          | Empty `<div>` with given height |
 * | `title`          | `<h1>` via `renderText` |
 * | `subTitle`       | `<h2>` via `renderText` |
 * | `text`           | `<p>` via `renderText` |
 * | `description`    | `<p>` via `transformDescription` |
 * | `imageUrl`       | `CustImage` |
 * | `imagePanel`     | Nested `Panel` |
 * | `videoUrl`       | `CustVideo` |
 * | `videoPanel`     | Nested `Panel` |
 * | `button`         | `CustButton` |
 * | `buttons`        | Row of `CustButton` elements |
 * | `panels`         | Horizontal row of child `Panel` elements |
 * | `rows`           | Vertical stack of child `Panel` elements |
 * | `items`          | Inline list of string values or child `Panel` elements |
 * | `itemsGrid`      | `ItemsGrid` component |
 * | `gridItems`      | Alias for `itemsGrid` |
 *
 * **Key deduplication:** Append a number suffix to disambiguate repeated keys,
 * e.g. `title1`, `title2`. The numeric suffix is stripped before dispatch.
 *
 * **Mobile styles:** `uiCompType.mStyles` overrides `uiCompType.styles` on
 * `deviceType === 'Mobile'`.
 *
 * **Style isolation:** A `childUiCompType` object (uiCompType *without* `styles`)
 * is passed to child components to prevent the panel's outer styles from leaking
 * into child elements.
 *
 * **Link wrapping:** If `sectionData.link` is present the entire panel is wrapped
 * in a Next.js `<Link>` with optional `newTab` support.
 *
 * Used by: `renderSection` (fallback), `ContentOnImage`, `Slider`,
 * `CustCarousel`, `Accordion`, and almost every other component for nested panels.
 */
import React, { useState, useEffect, useMemo } from "react";
import cssStyles from "./cms_renderer.module.scss";
import { SectionData, ComponentRenderProps, renderFunctions, renderSection } from "./index";
import { extractKeyStyles } from "./shared";
import { useDeviceType } from "@/hooks/useDeviceType";
import { renderText } from "./index";

interface PanelProps {
    sectionData: SectionData;
    props: ComponentRenderProps;
}

const Panel = ({ sectionData, props }: PanelProps): JSX.Element | null => {
    //console.log("Panel : ", sectionData, props);
    const panelClassName = "panel";
    const deviceType = useDeviceType();

    // Capture styles for the panel div WITHOUT mutating sectionData
    const baseStyles = useMemo(() => ({ ...sectionData.uiCompType?.styles }), [sectionData.uiCompType?.styles]);
    const mobileStyles = sectionData.uiCompType?.mStyles;
    const [styles, setStyles] = useState(baseStyles);

    // Create a uiCompType copy WITHOUT styles for child components (prevents panel styles leaking to text children)
    // This replaces the old destructive `delete sectionData.uiCompType?.styles`
    const childUiCompType = useMemo(() => {
        if (!sectionData.uiCompType) return {};
        const { styles: _panelStyles, ...rest } = sectionData.uiCompType;
        return rest;
    }, [sectionData.uiCompType]);

    useEffect(() => {
        if (deviceType === "Mobile" && mobileStyles) {
            setStyles({ ...baseStyles, ...mobileStyles });
        } else {
            setStyles(baseStyles);
        }
    }, [deviceType, baseStyles, mobileStyles]);

    const preparePanelContent = () => { return(
        <div id={sectionData.trackId || ('panel_')} className={cssStyles[panelClassName]} style={styles}>
            {Object.keys(sectionData).map((key: string) => {
                const matchedValue = sectionData[key];
                if (key === 'uiCompType' || key === 'link' || key === 'newTab' || key === 'trackInfo' || key === 'trackId') return null;
                const [keyWithExcludedStyles, ...styleParts] = key.split('-');
                const keyWithoutSeqNumber = keyWithExcludedStyles.replace(/\d+$/, '');
                const extracedStyles = extractKeyStyles(styleParts);
                let renderPanelBody = null;
                switch (keyWithoutSeqNumber) {
                    case 'space':
                        renderPanelBody = <div key={key} style={{ height: matchedValue }} />;
                        break;
                    case 'h1':
                        renderPanelBody = renderText(matchedValue, 'title', childUiCompType, props, 'h1', extracedStyles);
                        break;
                    case 'title':
                        renderPanelBody = renderText(matchedValue, 'title', childUiCompType, props, 'h2', extracedStyles);
                        break;
                    case 'subTitle':
                        renderPanelBody = renderText(matchedValue, 'subTitle', childUiCompType, props, 'h3', extracedStyles);
                        break;
                    case 'text':
                        renderPanelBody = renderText(matchedValue, 'text', childUiCompType, props, 'span', extracedStyles);
                        break;
                    case 'description':
                        renderPanelBody = renderText(matchedValue, 'description', childUiCompType, props, undefined, extracedStyles);
                        break;
                    case 'imageUrl':
                        const CustImageComponent = renderFunctions.CustImage;
                        renderPanelBody = <CustImageComponent sectionData={{ imageUrl: matchedValue } as SectionData} props={props} />;
                        break;
                    case 'imagePanel':
                        const CustImageComponent2 = renderFunctions.CustImage;
                        renderPanelBody = matchedValue &&
                            <CustImageComponent2 sectionData={{
                                ...matchedValue,
                                uiCompType: {
                                    ...matchedValue.uiCompType,
                                    styles: matchedValue.uiCompType?.styles,
                                    parentStyles: matchedValue.uiCompType?.parentStyles,
                                    containerStyles: matchedValue.uiCompType?.containerStyles
                                }
                            } as SectionData} props={props} />;
                        break;
                    case 'videoUrl':
                        const CustVideoComponent = renderFunctions.CustVideo;
                        renderPanelBody = <CustVideoComponent sectionData={{ videoUrl: matchedValue } as SectionData} props={props} />;
                        break;
                    case 'videoPanel':
                        const CustVideoComponent2 = renderFunctions.CustVideo;
                        renderPanelBody = matchedValue &&
                            <CustVideoComponent2 sectionData={{
                                ...matchedValue,
                                uiCompType: {
                                    ...matchedValue.uiCompType,
                                    styles: matchedValue.uiCompType?.styles,
                                    parentStyles: matchedValue.uiCompType?.parentStyles,
                                    containerStyles: matchedValue.uiCompType?.containerStyles
                                }
                            } as SectionData} props={props} />;
                        break;
                    case 'button':
                        const CustButtonComponent = renderFunctions.CustButton;
                        renderPanelBody = matchedValue && (
                            <div key={key} className={cssStyles[panelClassName + "Button"]} style={matchedValue.uiCompType?.parentStyles}>
                                <CustButtonComponent sectionData={matchedValue as SectionData} props={props} />
                            </div>
                        );
                        break;
                    case 'buttons':
                        const CustButtonComponent2 = renderFunctions.CustButton;
                        const buttons = matchedValue as SectionData[];
                        renderPanelBody = buttons.length > 0 && (
                            <div key={key} className={cssStyles[panelClassName + "Buttons"]} style={buttons[0].uiCompType?.parentStyles}>
                                {buttons.map((button: SectionData, index: number) => (
                                    <CustButtonComponent2 key={index} sectionData={button} props={props} index={index} />
                                ))}
                            </div>
                        );
                        break;
                    case 'panels':
                        renderPanelBody = (matchedValue as SectionData[])?.map((item: SectionData, index: number) => (
                            renderSection(item.trackId!, item, props)
                        ));
                        break;
                    case 'rows':
                        renderPanelBody = (matchedValue as SectionData[])?.map((item: SectionData, index: number) => (
                            renderSection(item.trackId!, item, props)
                        ));
                        break;
                    case 'items':
                        renderPanelBody = (matchedValue as SectionData[])?.map((item: SectionData, index: number) => (
                            renderSection(item.trackId!, item, props)
                        ));
                        break;
                    case 'itemsGrid':
                    case 'gridItems':
                        const ItemsGridComponent = renderFunctions.ItemsGrid;
                        const uiCompType = {
                            options: {
                                splitRaito: sectionData.uiCompType?.options?.splitRaito,
                                grid: sectionData.uiCompType?.options?.grid || 'spread',
                                spacing: sectionData.uiCompType?.options?.spacing || 2
                            },
                            styles: sectionData.uiCompType?.parentStyles
                        };
                        const gridSectionData: SectionData = {
                            items: matchedValue as SectionData[],
                            uiCompType: uiCompType
                        };
                        renderPanelBody = <ItemsGridComponent sectionData={gridSectionData} props={props} />;
                        break;
                    default:
                        const otherObject = (sectionData as any)[key] as SectionData;
                        if (typeof otherObject === 'object') {
                            renderPanelBody = renderSection(otherObject.trackId ?? key, otherObject, props);
                        }
                        break;
                }
                return <React.Fragment key={key}>{renderPanelBody}</React.Fragment>;
            })}
        </div>
    )};
    return (     
        <>       
            {sectionData.link ? (
            <a href={sectionData.link} target={sectionData.newTab ? "_blank" : "_self"} id={sectionData.trackId || ('panel_')}
                style={{ textDecoration: 'none', color: 'inherit', display: 'block' }}
                onClick={(e) => {
                    // trackItem(sectionData.trackInfo || { event: 'panelLinkClick', section: sectionData.trackId });
                }}
            >
                {preparePanelContent()}
            </a>
        ) : (preparePanelContent())}
        </>
    )
};
export default Panel;
